<script setup lang="ts">
/**
 * A thin Quill wrapper for the one field in the admin panel that stores rich
 * text — a Legal document's description. Every other editable text field here
 * (FAQ answers, News summary/content) stays plain, on purpose — see
 * `ContentPane.vue`'s own notes on why Legal is the one exception.
 *
 * `v-model` over an HTML string, not a Quill Delta: the stored `LegalPage.content`
 * column is HTML (rendered through `sanitizeHtml()` at read time in
 * `LegalOverlay.vue`/`profile/LegalPane.vue`), so this component's only job is
 * to get an operator from that HTML to a better version of it and back.
 */
import { onBeforeUnmount, onMounted, ref, watch } from 'vue';
import Quill from 'quill';
import 'quill/dist/quill.snow.css';

const props = defineProps<{ modelValue: string }>();
const emit = defineEmits<{ 'update:modelValue': [value: string] }>();

const container = ref<HTMLElement | null>(null);
let quill: Quill | null = null;

onMounted(() => {
  if (!container.value) return;
  quill = new Quill(container.value, {
    theme: 'snow',
    modules: {
      toolbar: [
        ['bold', 'italic', 'underline'],
        [{ header: [2, 3, false] }],
        [{ list: 'ordered' }, { list: 'bullet' }],
        ['link'],
        ['clean'],
      ],
    },
  });
  if (props.modelValue) quill.clipboard.dangerouslyPasteHTML(props.modelValue);
  quill.on('text-change', () => {
    emit('update:modelValue', quill?.root.innerHTML ?? '');
  });
});

/**
 * Only for a value set from *outside* this component (e.g. switching which
 * document the edit modal is showing) — its own edits already flow out via
 * `text-change` above, and re-applying our own emitted HTML back into Quill on
 * every keystroke would fight the cursor.
 */
watch(
  () => props.modelValue,
  (next) => {
    if (quill && next !== quill.root.innerHTML) {
      quill.clipboard.dangerouslyPasteHTML(next);
    }
  },
);

onBeforeUnmount(() => {
  quill = null;
});
</script>

<template>
  <div ref="container" class="editor" data-test="rich-text-editor"></div>
</template>

<style scoped>
/* Quill's own "snow" theme chrome is imported above; this just fits it into
   the admin panel's dark palette rather than its default light one. */
.editor :deep(.ql-toolbar) {
  border-color: var(--steel-600);
  background: var(--steel-800);
  border-radius: 0.2rem 0.2rem 0 0;
}
.editor :deep(.ql-container) {
  border-color: var(--steel-600);
  border-radius: 0 0 0.2rem 0.2rem;
  background: var(--steel-900);
  color: var(--steel-200);
  font-size: 0.85rem;
  min-height: 10rem;
}
.editor :deep(.ql-editor) {
  min-height: 10rem;
}
.editor :deep(.ql-stroke) {
  stroke: var(--steel-400);
}
.editor :deep(.ql-fill) {
  fill: var(--steel-400);
}
.editor :deep(.ql-picker-label) {
  color: var(--steel-400);
}
.editor :deep(.ql-toolbar button:hover .ql-stroke),
.editor :deep(.ql-toolbar button.ql-active .ql-stroke) {
  stroke: var(--accent);
}
.editor :deep(.ql-toolbar button:hover .ql-fill),
.editor :deep(.ql-toolbar button.ql-active .ql-fill) {
  fill: var(--accent);
}
</style>
